import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { makeStyles } from '../../utils/hooks/makeStyles'

interface WhatsAppMessageModalProps {
  open: boolean
  onClose: () => void
  patient: any
  onSubmit: (patientInfo: any, mobileNumber: string) => void
}

function WhatsAppMessageModal({
  open,
  onClose,
  patient,
  onSubmit,
}: WhatsAppMessageModalProps) {
  const classes = useStyles()
  const [mobileNumber, setMobileNumber] = useState(patient?.phoneNumber)

  // Reset mobile number whenever modal opens fresh
  useEffect(() => {
    if (open) setMobileNumber(patient?.phoneNumber || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const isMobileValid = /^\d{10}$/.test(mobileNumber)

  const handleSubmit = () => {
    if (!isMobileValid) return
    onSubmit(patient, mobileNumber)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.titleRow}>
          <WhatsAppIcon className={classes.whatsappIcon} />
          <Typography variant='h6' component='span'>
            Send WhatsApp Message
          </Typography>
        </Box>
        <IconButton
          aria-label='close'
          onClick={onClose}
          className={classes.closeBtn}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Patient Info */}
        <Box className={classes.infoCard}>
          <Typography variant='subtitle2' className={classes.infoLabel}>
            Patient Name
          </Typography>
          <Typography variant='body1' className={classes.infoValue}>
            {patient?.patientName || '—'}
          </Typography>

          <Divider className={classes.divider} />

          <Typography variant='subtitle2' className={classes.infoLabel}>
            Phone Number
          </Typography>
          <Typography variant='body1' className={classes.infoValue}>
            {patient?.phoneNumber || '—'}
          </Typography>

          <Divider className={classes.divider} />

          <Typography variant='subtitle2' className={classes.infoLabel}>
            UHID
          </Typography>
          <Typography variant='body1' className={classes.infoValue}>
            {patient?.patientUhid || '—'}
          </Typography>

          <Divider className={classes.divider} />

          <Typography variant='subtitle2' className={classes.infoLabel}>
            Employee ID
          </Typography>
          <Typography variant='body1' className={classes.infoValue}>
            {patient?.employeeId || '—'}
          </Typography>

          <Divider className={classes.divider} />

          <Typography variant='subtitle2' className={classes.infoLabel}>
            Status
          </Typography>
          <Typography variant='body1' className={classes.abnormalBadge}>
            {patient?.status || '—'}
          </Typography>
        </Box>

        {/* Mobile Number Input */}
        <TextField
          label='Mobile Number'
          fullWidth
          margin='normal'
          value={mobileNumber}
          onChange={(e) => {
            const val = e?.target?.value?.replace(/\D/g, '')?.slice(0, 10)
            setMobileNumber(val)
          }}
          placeholder='Enter 10-digit mobile number'
          inputProps={{ maxLength: 10, inputMode: 'numeric' }}
          error={mobileNumber?.length > 0 && !isMobileValid}
          helperText={
            mobileNumber?.length > 0 && !isMobileValid
              ? 'Enter a valid 10-digit mobile number'
              : ''
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color='secondary'>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={!isMobileValid}
          startIcon={<WhatsAppIcon />}
          className={classes.submitBtn}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default WhatsAppMessageModal

const useStyles = makeStyles(() => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: '8px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  whatsappIcon: {
    color: '#25D366',
    fontSize: '28px',
  },
  closeBtn: {
    position: 'absolute' as const,
    right: 8,
    top: 8,
  },
  dialogContent: {
    paddingTop: '8px !important',
  },
  infoCard: {
    background: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '4px',
  },
  infoLabel: {
    color: '#888',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    marginBottom: '2px',
  },
  infoValue: {
    fontWeight: 500,
    marginBottom: '4px',
  },
  divider: {
    margin: '8px 0',
  },
  abnormalBadge: {
    display: 'inline-block',
    background: '#fff3e0',
    fontWeight: 700,
    borderRadius: '4px',
    padding: '2px 10px',
    fontSize: '13px',
  },
  submitBtn: {
    background: '#25D366',
    color: '#fff',
    '&:hover': {
      background: '#1ebe5d',
    },
    '&:disabled': {
      background: '#ccc',
      color: '#fff',
    },
  },
}))
